import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import gazetteer, { CountryCodes, type Market } from '@bridebook/toolbox/src/gazetteer';
import { Box, Button, Well } from '@bridebook/ui';
import InfoRow from '@bridebook/web/app-shared/components/supplier/sections/info-row/info-row';
import SectionHeading from '@bridebook/web/app-shared/components/supplier/sections/section-heading';
import componentStyles from '@bridebook/web/app-basic/components/supplier/sections/pricing/supplier-pricing.style';

interface SupplierPricingProps {
  info?: string;
  market: Market;
  maxPrice?: number;
  minPrice?: number;
  showAllInfoText: boolean;
  supplierCountryCode?: CountryCodes;
}

const SupplierPricing = ({
  info,
  market,
  maxPrice,
  minPrice,
  showAllInfoText,
  supplierCountryCode,
}: SupplierPricingProps) => {
  const { t } = useTranslation('supplier');
  const [fullTextVisible, setFullTextVisible] = useState(true);
  const charLimit = 130;
  const readMoreCaption = fullTextVisible ? t('button.readMore') : t('button.readLess');

  if (!supplierCountryCode) return null;

  const currency = gazetteer.getMarketByCountry(supplierCountryCode).currency;
  const items: { title: string; amount?: string; visible: boolean }[] = [
    {
      title: t('supplier.pricing.priceStart'),
      amount: market.formatCurrency(minPrice ?? 0, { currency, maximumFractionDigits: 0 }),
      visible: minPrice !== undefined && minPrice > 0,
    },
    {
      title: t('supplier.pricing.maxPrice'),
      amount: market.formatCurrency(maxPrice ?? 0, { currency, maximumFractionDigits: 0 }),
      visible: maxPrice !== undefined && maxPrice > 0,
    },
  ].filter((item) => item.visible);

  const styles = componentStyles();

  return (
    <Box pb={4}>
      <SectionHeading largeMargin>
        <Box>{t('supplier.pricing.title')}</Box>
      </SectionHeading>
      {items.length > 0 && (
        <Well theme="yellow" size="full">
          {items.map((item, index) => (
            <Box style={styles.wellInfoWrap} key={index}>
              <InfoRow {...item} />
            </Box>
          ))}
        </Well>
      )}
      {info && (
        <Box style={styles.infoText}>
          {showAllInfoText && fullTextVisible && info.length > charLimit
            ? `${info.substring(0, charLimit)}...`
            : info}
          {info.length > charLimit && showAllInfoText && (
            <Box style={styles.moreWrap}>
              <Button
                theme="ghost"
                text={readMoreCaption}
                onClick={() => {
                  setFullTextVisible(!fullTextVisible);
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SupplierPricing;
